import { useEffect, useState } from 'react';
import { Flex, SelectInput, Spacer } from 'vcc-ui';

import TextButton from 'atoms/TextButton';
import { ApiOption } from 'features/test-access-tokens/test-access-tokens.types';
import useAuthProtected, { AuthModalConfig } from 'libs/auth/useAuthProtected';

import * as styles from './ApiSelect.styles';

export type ApiSelectProps = {
  buttonText: string;
  options: ApiOption[];
  isLoading?: boolean;
  authProtectedConfig?: AuthModalConfig;
  onGenerate: (apiId: string) => void;
};

const baseSelectParams = {
  label: 'Select API here...',
  description: 'Select which API you want to generate an access token for.',
  error: 'You have to select an API to generate a token',
};

const ApiSelect: React.FC<ApiSelectProps> = ({
  buttonText,
  options,
  isLoading = false,
  onGenerate,
  authProtectedConfig,
}) => {
  const [api, setApi] = useState<string | undefined>(undefined);
  const [valid, setValid] = useState(true);
  const disabled = options.length === 0;

  const protect = useAuthProtected(authProtectedConfig);

  const onSelectChange = (apiId: string) => {
    setValid(true);
    setApi(apiId);
  };

  const withAuthProtect = (callback: () => void, shouldProtect: boolean) =>
    shouldProtect ? protect(() => callback()) : callback();

  const generate = () => {
    if (!api) {
      setValid(false);
    } else {
      withAuthProtect(() => {
        onGenerate(api);
        setApi(undefined);
      }, !!authProtectedConfig);
    }
  };

  useEffect(() => {
    setApi(undefined);
  }, [options]);

  return (
    <Flex extend={styles.outerWrapper}>
      <Flex extend={styles.selectWrapper}>
        <SelectInput
          allowEmpty
          value={api}
          label={baseSelectParams.label}
          description={valid ? baseSelectParams.description : undefined}
          errorMessage={!valid ? baseSelectParams.error : undefined}
          isValid={valid}
          loading={isLoading}
          disabled={disabled || isLoading}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void =>
            onSelectChange(e.currentTarget.value)
          }
        >
          {options.map(({ key, name }) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </SelectInput>
      </Flex>
      <Spacer size={[1, 0]} />
      <Flex extend={styles.clickWrapper}>
        <TextButton
          onClick={generate}
          disabled={disabled || isLoading}
          trackEventLabel={api}
        >
          {buttonText}
        </TextButton>
      </Flex>
    </Flex>
  );
};

export default ApiSelect;
